// src/components/admin/MapChart.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Card, Spin, Empty, Modal, Typography, Button, message } from "antd";
import Globe from "react-globe.gl"; // install react-globe.gl + three

const { Title, Text, Paragraph } = Typography;

/**
 * MapChart (Globe) - admin visualization using react-globe.gl + Ant Design
 *
 * Props:
 * - points: array of { lat, lng, value, label, tagline?, city?, state? }
 * - height: CSS height of the globe container (default: 480)
 * - accentColor: color for plotted points
 * - autoRotate: boolean default true
 * - maxAltitude: numeric cap for altitude scaling (relative)
 * - onPointClick: optional callback (point) => void
 *
 * Notes:
 * - Ensure react-globe.gl and three are installed:
 *   npm install react-globe.gl three --legacy-peer-deps
 */
export default function MapChart({
  points = [],
  height = 480,
  accentColor = "#6D28D9",
  autoRotate = true,
  maxAltitude = 0.06,
  onPointClick,
}) {
  const globeRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const [webglSupported, setWebglSupported] = useState(true);
  const [hovered, setHovered] = useState(null);
  const [loading, setLoading] = useState(true);

  // Simple WebGL detection
  useEffect(() => {
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
      if (!ctx) setWebglSupported(false);
    } catch (err) {
      setWebglSupported(false);
    }
  }, []);

  // Initialize globe controls when ready
  useEffect(() => {
    if (!webglSupported) {
      setLoading(false);
      return;
    }
    const g = globeRef.current;
    if (!g) return;

    // small delay until globe initializes
    const t = setTimeout(() => {
      // enable/disable autoRotate
      try {
        // react-globe exposes controls() when mounted
        const controls = g && g.controls ? g.controls() : null;
        if (controls && typeof controls.autoRotate !== "undefined") {
          controls.autoRotate = !!autoRotate;
          // adjust speed mildly
          controls.autoRotateSpeed = 0.6;
        }
      } catch (e) {
        // ignore
      }
      setLoading(false);
    }, 300);

    return () => clearTimeout(t);
  }, [webglSupported, autoRotate]);

  // Memoize points data for performance
  const pointsData = useMemo(() => Array.isArray(points) ? points : [], [points]);

  // Scale altitude safely and cap
  const getAltitude = (d) => {
    try {
      // simple log scale then clamp
      const scaled = Math.log10((Number(d.value) || 1) + 1) / 3; // small scale
      return Math.min(maxAltitude, Math.max(0.002, scaled));
    } catch (e) {
      return 0.002;
    }
  };

  // Point radius (visual size)
  const getRadius = (d) => {
    try {
      // radius in pixels relative to value (keep small)
      return Math.max(0.2, Math.log10((Number(d.value) || 1) + 1) * 0.45);
    } catch (e) {
      return 0.3;
    }
  };

  const handlePointClick = (d) => {
    setSelected(d);
    if (typeof onPointClick === "function") onPointClick(d);
  };

  // pause rotatation on hover (UX)
  useEffect(() => {
    if (!globeRef.current || !webglSupported) return;
    const g = globeRef.current;
    const controls = g.controls ? g.controls() : null;
    if (!controls) return;

    if (hovered) {
      // pause
      try { controls.autoRotate = false; } catch (e) {}
    } else {
      try { controls.autoRotate = !!autoRotate; } catch (e) {}
    }
  }, [hovered, autoRotate, webglSupported]);

  // Graceful fallback: list top cities if no WebGL or user agent blocked it
  if (!webglSupported) {
    return (
      <Card style={{ borderRadius: 10 }}>
        <Title level={4}>Where our alumni are</Title>
        <Paragraph>Interactive globe is unavailable in your browser. Here's a quick list of top locations.</Paragraph>
        {loading ? <Spin /> : (
          <>
            {pointsData && pointsData.length ? (
              <ul>
                {pointsData.slice(0, 12).map((p, idx) => (
                  <li key={p.label || idx}><strong>{p.label}</strong> — {(p.value || 0).toLocaleString()} signups</li>
                ))}
              </ul>
            ) : (
              <Empty description="No location data available" />
            )}
          </>
        )}
      </Card>
    );
  }

  return (
    <Card style={{ borderRadius: 10 }} bodyStyle={{ padding: 0 }}>
      <div style={{ padding: 16, borderBottom: "1px solid #f0f0f0" }}>
        <Title level={4} style={{ margin: 0 }}>Where our alumni are</Title>
        <Text type="secondary">Explore signups visually — click a point to view details and invite classmates.</Text>
      </div>

      <div style={{ width: "100%", height }}>
        {loading && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5 }}>
            <Spin tip="Loading map…" />
          </div>
        )}

        <Globe
          ref={globeRef}
          globeImageUrl="//unpkg.com/three-globe/example/img/earth-dark.jpg"
          backgroundColor="#ffffff"
          pointsData={pointsData}
          pointLat="lat"
          pointLng="lng"
          pointAltitude={(d) => getAltitude(d)}
          pointRadius={(d) => getRadius(d)}
          pointColor={() => accentColor}
          pointsMerge={true}
          onPointClick={handlePointClick}
          onPointHover={(p) => setHovered(p)}
          pointLabel={(d) => {
            // label used by react-globe internal tooltip; still useful for accessibility
            return `${d.label || "Location"} — ${d.value?.toLocaleString?.() ?? d.value} signups`;
          }}
          // performance & rendering options
          enablePointerInteraction={true}
          showAtmosphere={true}
          atmosphereColor={accentColor}
          atmosphereAltitude={0.25}
          animateIn={true}
        />
      </div>

      {/* Selected point modal */}
      <Modal
        open={!!selected}
        title={selected ? selected.label : ""}
        onCancel={() => setSelected(null)}
        footer={[
          <Button key="close" onClick={() => setSelected(null)}>Close</Button>,
          <Button key="invite" type="primary" onClick={() => {
            // example invite action
            message.success(`Invite sent to alumni in ${selected?.label}`);
            setSelected(null);
          }}>Invite classmates</Button>
        ]}
        aria-labelledby="map-point-modal"
      >
        {selected && (
          <>
            <Paragraph><strong>Signups:</strong> {(selected.value || 0).toLocaleString()}</Paragraph>
            {selected.city && <Paragraph><strong>City:</strong> {selected.city}</Paragraph>}
            {selected.state && <Paragraph><strong>State:</strong> {selected.state}</Paragraph>}
            {selected.tagline && <Paragraph>{selected.tagline}</Paragraph>}
            <div style={{ marginTop: 12 }}>
              <Button type="default" onClick={() => navigator.clipboard?.writeText?.(selected.label || "") && message.success("Location copied")}>Copy location</Button>
            </div>
          </>
        )}
      </Modal>
    </Card>
  );
}

MapChart.propTypes = {
  points: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
      value: PropTypes.number,
      label: PropTypes.string,
      tagline: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
    })
  ),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  accentColor: PropTypes.string,
  autoRotate: PropTypes.bool,
  maxAltitude: PropTypes.number,
  onPointClick: PropTypes.func,
};
